import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** При смене ключа (например, адреса страницы) ошибка сбрасывается */
  resetKey?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/** Ошибка в одной странице не роняет всё приложение: показываем сообщение и кнопку «Повторить» */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Ошибка отображения страницы:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center px-6 text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl tone-destructive ring-1 ring-inset">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <h2 className="text-base font-semibold">Не удалось отобразить страницу</h2>
        <p className="mt-1 max-w-md text-sm text-muted-foreground">
          Произошла непредвиденная ошибка. Попробуйте обновить страницу — если ошибка повторится,
          сообщите администратору.
        </p>
        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={() => this.setState({ error: null })}>
            <RotateCcw className="h-4 w-4" />
            Повторить
          </Button>
          <Button onClick={() => window.location.reload()}>Обновить страницу</Button>
        </div>
      </div>
    );
  }
}
